import type { ThunkAction, AppState, ThunkExtras, GetState, APIError, Dispatch } from '../../types/app.types';
import type { V2OrdersResponse, V2OrderResponse, V2OrderItem, V2Coupon, V2Order } from '../../types/api-v2.types';

type RequestOrder = {
  type: 'orders/REQ_ORDER';
};
type RequestOrderSuccess = {
  type: 'orders/REQ_ORDER_SUCCESS';
  res: V2Order;
};
type RequestOrderError = {
  type: 'orders/REQ_ORDERS_ERROR';
} & APIError;
type GetAccountOrders = {
  type: 'account/ORDERS';
};
type GetAccountOrdersSuccess = {
  type: 'account/ORDERS_SUCCESS';
  res: Array<V2Order>;
};
type GetAccountOrdersError = {
  type: 'account/ORDERS_ERROR';
} & APIError;
type CancelMedicationPurchase = {
  type: 'orders/CANCEL_MEDICATION';
};
type CancelMedicationPurchasesSuccess = {
  type: 'orders/CANCEL_MEDICATION_SUCCESS';
  res: Record<string, any>;
};
type CancelMedicationPurchaseError = {
  type: 'orders/CANCEL_MEDICATION_ERROR';
} & APIError;
export type OrdersAction =
  | { type: '@INIT' }
  | RequestOrder
  | RequestOrderSuccess
  | RequestOrderError
  | GetAccountOrders
  | GetAccountOrdersSuccess
  | GetAccountOrdersError
  | CancelMedicationPurchase
  | CancelMedicationPurchasesSuccess
  | CancelMedicationPurchaseError;
export type MedicationPurchased = {
  name: string;
  formulation: string;
  price: string;
  discountPrice: string;
  coupon: V2Coupon | null | undefined;
  medId: string;
  medNameId: string;
  accountMedicationPurchaseId: string;
  status: string;
  filledOn: string | null | undefined;
};
export type OrdersState = Record<string, V2Order>;
export const orderItemsSummary = (items: Array<V2OrderItem> | null | undefined): string => {
  if (!items) return '';
  return items.reduce((acc: string, item: V2OrderItem, idx: number) => {
    if (idx === 0) return item.formattedName;
    if (idx === items.length - 1) return acc + ' & ' + item.formattedName;
    return acc + ', ' + item.formattedName;
  }, '');
};
export const getOrderFromState = (state: AppState, orderId: string): V2Order => {
  return state.orders[orderId];
};

const orderSuccess = (dispatch: Dispatch) => (res: V2Order) => {
  return dispatch({
    type: 'orders/REQ_ORDER_SUCCESS',
    res,
  });
};

const dispatchError = (dispatch: Dispatch) => (error: APIError) => {
  return dispatch({
    type: 'orders/REQ_ORDERS_ERROR',
    ...error,
  });
};

export function requestOrder(orderId: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get(`v2/orders/${orderId}`)
      .then((res) => res.data)
      .then((data: V2OrderResponse): V2Order => data.result)
      .then(orderSuccess(dispatch))
      .catch(dispatchError(dispatch));
  };
}

export function sendAccountMedicationPurchaseSMS(): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient.post('/v2/account/purchases/sms');
  };
}

export function getAccountOrders(): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    return extras.blinkAPIClient
      .get('v2/orders')
      .then((res) => res.data)
      .then((data: V2OrdersResponse): Array<V2Order> => data.result)
      .then((orders: Array<V2Order>) =>
        dispatch({
          type: 'account/ORDERS_SUCCESS',
          res: orders,
        })
      )
      .catch((err: APIError) =>
        dispatch({
          type: 'account/ORDERS_ERROR',
          ...err,
        })
      );
  };
}

export function cancelMedicationPurchase(
  accountMedicationPurchaseId: string,
  reason: string
): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    return extras.blinkAPIClient
      .put(`/account/medications/purchases/${accountMedicationPurchaseId}/cancel`, {
        cancellationReason: reason,
      })
      .then((res) =>
        dispatch({
          type: 'orders/CANCEL_MEDICATION_SUCCESS',
          res,
        })
      );
  };
}

export function cancelMedicationByOrderItemId(
  orderId: string,
  orderItemId: string,
  cancellationReason?: string
): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    return extras.blinkAPIClient
      .post(`v2/orders/${orderId}/order_item/${orderItemId}/cancel`, {
        cancellationReason,
      })
      .then((res) =>
        dispatch({
          type: 'orders/CANCEL_MEDICATION_SUCCESS',
          res,
        })
      );
  };
}

export const initialState: OrdersState = {};
export default function ordersReducer(state: OrdersState = initialState, action: OrdersAction): OrdersState {
  switch (action.type) {
    case 'orders/REQ_ORDER_SUCCESS': {
      const { res } = action;
      return { ...state, [res.id]: res };
    }

    case 'account/ORDERS_SUCCESS': {
      const { res } = action;
      const accountOrders = {};
      const orderedOrders = res.sort((a, b) => new Date(b.createdOn).valueOf() - new Date(a.createdOn).valueOf());

      for (const order of orderedOrders) {
        accountOrders[order.id] = order;
      }

      return { ...state, ...accountOrders };
    }

    default:
      return state;
  }
}
